import {useCallback, useRef, type Ref} from 'react'
import type {ConversationComment} from '../../page-data/payloads/conversation-comments'
import {ConversationCommentBox} from '@github-ui/diff-lines/conversation-comment-box'
import {NewMarkdownViewer, type NewMarkdownProps} from '@github-ui/markdown-viewer/NewMarkdownViewer'
import type {UseMutationResult} from '@github-ui/react-query'
import {Stack, Button, useMergedRefs} from '@primer/react'
import {clsx} from 'clsx'

import styles from './ConversationItemBody.module.css'
import {ReactionViewerBase} from '@github-ui/reaction-viewer/ReactionViewerBase'
import {
  applyVariablesToReactions,
  type ReactToIssueCommentResponse,
  type ReactToIssueCommentVariables,
} from '../../mutations/use-react-to-issue-comment-mutation'
import type {ReactionContent} from '@github-ui/reaction-viewer/ReactionGroupsUtils'
import type {ConversationItemBodyEditManager} from './use-body-edit-manager'
import {itemTypeName} from './utils/item-type-name'
import {ErrorBoundary} from '@github-ui/react-core/error-boundary'
import {ComposerFallback} from './ComposerFallback'
import {StaleContentError} from '@github-ui/pull-request-page-data-tooling/fetch-error-handling'
import type {CommentBoxHandle} from '@github-ui/comment-box/CommentBox'
import {FailureBanner} from '../FailureBanner'

type ReactToItemMutation = UseMutationResult<ReactToIssueCommentResponse, Error, ReactToIssueCommentVariables, unknown>

interface ConversationItemBodyProps {
  item: ConversationComment
  editorContainerRef: Ref<HTMLDivElement>
  editManager: ConversationItemBodyEditManager
  reactMutation: ReactToItemMutation
}

/** Editable item body and reactions for conversation items. */
export function ConversationItemBody({
  item,
  editorContainerRef,
  editManager,
  reactMutation,
}: ConversationItemBodyProps) {
  const optimisticReactionGroups = reactMutation?.isPending
    ? applyVariablesToReactions(item.reactionGroups, reactMutation.variables)
    : item.reactionGroups

  const onReact = (reaction: ReactionContent, viewerHasReacted: boolean) =>
    reactMutation?.mutate({
      commentId: String(item.id),
      reaction,
      viewerHasReacted,
    })

  /** Called when Markdown is updated without entering edit mode (ie, checkbox interaction). */
  const handleMarkdownChange = async (newValue: string) => {
    try {
      await editManager.handleDirectMarkdownChange(newValue)
    } catch {
      /* noop: edit manager handles errors internally */
    }
  }

  const typeName = itemTypeName(item)

  const commentBoxRef = useRef<CommentBoxHandle>(null)

  const errorBanner = editManager.error && (
    <FailureBanner
      onDismiss={editManager.dismissError}
      title={`Failed to update ${typeName.lowercase}`}
      returnFocusRef={commentBoxRef}
      error={editManager.error}
    />
  )
  const contentIsStale = editManager.error instanceof StaleContentError

  const autofocusRef = useCallback((editor: CommentBoxHandle | null) => editor?.focus(), [])
  const mergedEditorRef = useMergedRefs(autofocusRef, commentBoxRef)

  const markdownViewerProps: NewMarkdownProps = item.viewerCanUpdate
    ? {
        disabled: editManager.isPending || contentIsStale,
        verifiedHTML: editManager.optimisticBodyHtml,
        onChange: handleMarkdownChange,
        markdownValue: editManager.optimisticBody,
        viewerCanUpdate: true,
      }
    : {verifiedHTML: editManager.optimisticBodyHtml}

  return editManager.editingBody !== null ? (
    <ErrorBoundary
      fallback={
        <Stack align="end" gap="condensed" ref={editorContainerRef}>
          <ComposerFallback />
          <Button onClick={() => editManager.cancel({keepDraft: true})}>Cancel</Button>
        </Stack>
      }
      boundaryName="ConversationItemEditor"
    >
      <Stack direction="vertical" gap="condensed" ref={editorContainerRef}>
        {!contentIsStale && errorBanner}
        <ConversationCommentBox
          label={`Edit ${typeName.lowercase}`}
          value={editManager.editingBody}
          onChange={editManager.handleDraftChange}
          onSave={editManager.submit}
          validationResult={editManager.validationResult}
          onCancel={() => editManager.cancel({keepDraft: contentIsStale})}
          saveButtonText={`Update ${typeName.lowercase}`}
          maxHeightLines={15}
          ref={mergedEditorRef}
          contentIsStale={contentIsStale}
          saveButtonTrailingIcon={false}
          surface="issue_comment"
        />
      </Stack>
    </ErrorBoundary>
  ) : (
    <>
      <Stack direction="vertical" gap="condensed">
        {errorBanner}
        <NewMarkdownViewer
          className={clsx('f5', editManager.isPending ? styles.pendingComment : undefined)}
          {...markdownViewerProps}
        />
      </Stack>
      {item.reactionGroups.length > 0 && (
        <div className="mt-2">
          <ReactionViewerBase
            reactionGroups={optimisticReactionGroups}
            canReact={item.viewerCanReact}
            onReact={onReact}
          />
        </div>
      )}
    </>
  )
}
